// External Libraries
import React from "react";
import { Check, ChevronDown } from "lucide-react";
import * as Icon from "react-feather";
import clsx from "clsx";

// Services
import BookkeepingService from "../../../../services/BookkeepingService";
import ClientService from "../../../../services/ClientService";
import UtilityService from "../../../../services/UtilityService";

// Common Components
import WComponent from "../../../common/WComponent";
import SearchInput from "../../../common/search_input/SearchInput";
import Button from "../../../common/button/Button";
import Loader from "../../../common/loader/Loader";
import Checkbox from "../../../common/checkbox/Checkbox";
import Modal from "react-responsive-modal";
import ClassificationDropdown from "./ClassificationDropdown";

// Constants
import TAX_CATEGORIES from "../../../../constants/Taxes";
class BookkeepingTable extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			// Input State
			industry: props.initialIndustry || "",
			province: props.initialProvince || "",
			bankFilter: "",

			// Data State
			classifyingAi: false,
			selectAll: false,
			activeClassificationDropdown: null,
			bulkClassificationDropdownOpen: false,
			search: "",
			classificationFilter: "",
			classifications: [
				{ name: "Accounts receivable (A/R)", classification: "Balance Sheet" },
				{ name: "Current assets", classification: "Balance Sheet" },
				{ name: "Non current assets", classification: "Balance Sheet" },
				{ name: "Cash and cash equivalents", classification: "Balance Sheet" },
				{ name: "Inventory", classification: "Balance Sheet" },
				{ name: "Prepaid expenses", classification: "Balance Sheet" },
				{ name: "Property, plant, and equipment (PP&E)", classification: "Balance Sheet" },
				{ name: "Long-term assets", classification: "Balance Sheet" },
				{ name: "Accounts payable (A/P)", classification: "Balance Sheet" },
				{ name: "Current liabilities", classification: "Balance Sheet" },
				{ name: "Long-term liabilities", classification: "Balance Sheet" },
				{ name: "Equity", classification: "Balance Sheet" },
				{ name: "Retained earnings", classification: "Balance Sheet" },
				{ name: "Advertising", classification: "Income" },
				{ name: "Auto", classification: "Income" },
				{ name: "Bad debts", classification: "Income" },
				{ name: "Bank charges", classification: "Income" },
				{ name: "Donations", classification: "Income" },
				{ name: "Cost of Goods Sold", classification: "Income" },
				{ name: "Cost of Labour", classification: "Income" },
				{ name: "Distribution costs", classification: "Income" },
				{ name: "Dues and subscriptions", classification: "Income" },
				{ name: "Dividends", classification: "Income" },
				{ name: "Entertainment", classification: "Income" },
				{ name: "Equipment rental", classification: "Income" },
				{ name: "Equipment rental - COS", classification: "Income" },
				{ name: "Insurance", classification: "Income" },
				{ name: "Interest paid", classification: "Income" },
				{ name: "Legal and professional fees", classification: "Income" },
				{ name: "Meals and entertainment", classification: "Income" },
				{ name: "Office/General Administrative Income", classification: "Income" },
				{ name: "Other Miscellaneous Service Costs", classification: "Income" },
				{ name: "Payroll Expenses", classification: "Income" },
				{ name: "Promotional Meals", classification: "Income" },
				{ name: "Rent or Lease of Buildings", classification: "Income" },
				{ name: "Sales of Product Income", classification: "Income" },
				{ name: "Service/Fee Income", classification: "Income" },
				{ name: "Shareholder Loan/(s)", classification: "Balance Sheet"},
				{ name: "Repairs and Maintenance", classification: "Income" },
				{ name: "Shipping, Freight, and Delivery", classification: "Income" },
				{ name: "Subscriptions", classification: "Income" },
				{ name: "Supplies", classification: "Income" },
				{ name: "Taxes", classification: "Income" },
				{ name: "Travel", classification: "Income" },
				{ name: "Utilities", classification: "Income" },
				{ name: "Uncategorized", classification: "Income" }
			],
			priorYearTrialBalance: [],
			hasTax: props.initialHasTax !== undefined ? props.initialHasTax : true,
			bookkeepingTableHeadings: [
				{ name: "Date", style: { flex: 1.2 } },
				{ name: "Description", style: { flex: 3 } },
				{ name: "Amount", style: { flex: 1 } },
				{ name: "Tax", style: { flex: 1.2 } },
				{ name: "Tax Amount", style: { flex: 1 } },
				{ name: "Classification", style: { flex: 2.5 } },
				{ name: "Confidence", style: { flex: 0.8 } },
				{ name: "Bank", style: { flex: 1.5 } }
			],
			generalLedgerReferences: {},
			openAiAccountsTaxClassifications: [],
			classificationTaxMap: {},
			classificationTaxModalOpen: false,
			taxSettingsLoading: false,
			taxModalSearch: "",
			addClassificationModalOpen: false,
			newClassificationName: "",
			newClassificationTax: "",

			// UI State
			loading: false,
			sortField: null,
			sortOrder: "asc",
			showUnclassifiedOnly: false
		};
	}

	async componentDidMount() {
		const { clientId, initialCwFiles } = this.props;

		const savedCustom = JSON.parse(localStorage.getItem(`bookkeeping_custom_classifications_${clientId}`) || "[]");
		const savedTaxMap = JSON.parse(localStorage.getItem(`bookkeeping_tax_map_${clientId}`) || "{}");

		if (savedCustom.length > 0 || Object.keys(savedTaxMap).length > 0) {
			await this.update({
				classifications: [...this.state.classifications, ...savedCustom],
				classificationTaxMap: savedTaxMap,
			});
		}

		if (initialCwFiles && initialCwFiles.length > 0) {
			await this.processReferenceFiles(initialCwFiles);
		}
	}

	handleSelectAll = async event => {
		const { checked } = event.target;
		const { transactions, onTransactionsUpdate } = this.props;

		const filteredTransactions = this.getFilteredTransactions();
		const filteredIndexes = this.getFilteredIndexes(filteredTransactions);

		const updatedTransactions = transactions.map((transaction, index) => ({
			...transaction,
			selected: filteredIndexes.has(index) ? checked : transaction.selected
		}));

		await this.update({
			selectAll: checked
		});

		onTransactionsUpdate(updatedTransactions);
	};

	handleSelectRow = async transaction => {
		const { transactions, onTransactionsUpdate } = this.props;

		const updatedTransactions = transactions.map(t => {
			const newSelected = t === transaction ? !t.selected : t.selected;
			return { ...t, selected: newSelected };
		});

		const allSelected = updatedTransactions.every(t => t.selected);

		await this.update({ selectAll: allSelected });

		onTransactionsUpdate(updatedTransactions);
	};

	handleClassificationClick = async (transaction, event) => {
		const { activeClassificationDropdown } = this.state;

		event.stopPropagation();

		await this.update({
			activeClassificationDropdown: activeClassificationDropdown === transaction ? null : transaction
		});
	};

	handleClassificationChange = async (transaction, newClassification) => {
		const { transactions, onTransactionsUpdate } = this.props;
		const { classificationTaxMap } = this.state;

		const autoTax = classificationTaxMap[newClassification] || "";

		const updatedTransactions = transactions.map(t =>
			t === transaction ? { ...t, classification: newClassification, tax: autoTax } : t
		);

		await this.update({ activeClassificationDropdown: null });

		onTransactionsUpdate(updatedTransactions);
	};

	handleBulkClassificationClick = async event => {
		event.stopPropagation();

		await this.update({
			bulkClassificationDropdownOpen: !this.state.bulkClassificationDropdownOpen
		});
	};

	handleBulkClassificationChange = async newClassification => {
		const { transactions, onTransactionsUpdate } = this.props;
		const { classificationTaxMap } = this.state;

		const filteredTransactions = this.getFilteredTransactions();
		const filteredIndexes = this.getFilteredIndexes(filteredTransactions);

		const autoTax = classificationTaxMap[newClassification] || "";

		const updatedTransactions = transactions.map((transaction, index) =>
			filteredIndexes.has(index) && transaction.selected ? { ...transaction, classification: newClassification, tax: autoTax } : transaction
		);

		await this.update({
			bulkClassificationDropdownOpen: false
		});

		onTransactionsUpdate(updatedTransactions);
	};

	handleClickOutside = async () => {
		await this.update({
			activeClassificationDropdown: null,
			bulkClassificationDropdownOpen: false
		});
	};

	openClassificationTaxModal = () => this.update({ classificationTaxModalOpen: true });
	closeClassificationTaxModal = () => this.update({ classificationTaxModalOpen: false, taxModalSearch: "" });

	openAddClassificationModal = () => this.update({ addClassificationModalOpen: true, newClassificationName: "", newClassificationTax: "" });
	closeAddClassificationModal = () => this.update({ addClassificationModalOpen: false });
	onAddClassification = () => {
		const { newClassificationName, newClassificationTax, classifications, classificationTaxMap } = this.state;
		const { clientId } = this.props;
		if (!newClassificationName.trim()) return;
		const name = newClassificationName.trim();
		const newEntry = { name, classification: "Income" };
		const updatedMap = { ...classificationTaxMap };
		if (newClassificationTax) updatedMap[name] = newClassificationTax;

		const savedCustom = JSON.parse(localStorage.getItem(`bookkeeping_custom_classifications_${clientId}`) || "[]");
		localStorage.setItem(`bookkeeping_custom_classifications_${clientId}`, JSON.stringify([...savedCustom, newEntry]));
		localStorage.setItem(`bookkeeping_tax_map_${clientId}`, JSON.stringify(updatedMap));

		this.update({
			classifications: [...classifications, newEntry],
			classificationTaxMap: updatedMap,
			addClassificationModalOpen: false,
		});
	};

	onFetchAllTaxCodes = async () => {
		const { clientId } = this.props;
		const { classificationTaxMap, industry, province } = this.state;
		const sortedClassifications = this.getSortedClassifications();

		const missing = sortedClassifications.filter(c => !classificationTaxMap[c.name]);

		if (!missing.length) return;

		await this.update({ taxSettingsLoading: true });

		const results = await Promise.all(
			missing.map(c => BookkeepingService.getTaxRates({ clientId, account: c.name, province, industry }))
		);

		const newEntries = Object.fromEntries(
			missing.map((c, i) => [c.name, results[i]]).filter(([, tax]) => tax)
		);

		const mergedMap = { ...this.state.classificationTaxMap, ...newEntries };
		localStorage.setItem(`bookkeeping_tax_map_${clientId}`, JSON.stringify(mergedMap));

		await this.update({
			classificationTaxMap: mergedMap,
			taxSettingsLoading: false
		});
	};

	handleClassificationTaxChange = async (classificationName, tax) => {
		const { classificationTaxMap } = this.state;
		const { clientId, transactions, onTransactionsUpdate } = this.props;
		const updatedMap = { ...classificationTaxMap, [classificationName]: tax };

		localStorage.setItem(`bookkeeping_tax_map_${clientId}`, JSON.stringify(updatedMap));

		await this.update({ classificationTaxMap: updatedMap });

		const updatedTransactions = transactions.map(t =>
			t.classification === classificationName ? { ...t, tax } : t
		);
		onTransactionsUpdate(updatedTransactions);
	};

	onSearch = async event => {
		await this.update({
			search: event.target.value
		});
	};

	getFilteredTransactions = () => {
		const { search, classificationFilter, bankFilter, showUnclassifiedOnly } = this.state;
		const { transactions } = this.props;

		let filteredTransactions = transactions;

		if (showUnclassifiedOnly) {
			filteredTransactions = filteredTransactions.filter(t => !t.classification || t.classification === "Unclassified" || t.classification === "Uncategorized");
		}

		if (search) {
			const searchTerm = search.toLowerCase();
			filteredTransactions = filteredTransactions.filter(
				(transaction) =>
					transaction.description.toLowerCase().includes(searchTerm) ||
					(transaction.merchant || "").toLowerCase().includes(searchTerm)
			);
		}

		if (classificationFilter) {
			const classificationTerm = classificationFilter.toLowerCase();
			filteredTransactions = filteredTransactions.filter((transaction) => {
				const classification = transaction.classification || "Unclassified";
				return classification.toLowerCase().includes(classificationTerm);
			});
		}

		if (bankFilter) {
			const bankTerm = bankFilter.toLowerCase();
			filteredTransactions = filteredTransactions.filter(
				(transaction) =>
					transaction.accountName &&
					transaction.accountName.toLowerCase().includes(bankTerm)
			);
		}

		return filteredTransactions;
	};

	getFilteredIndexes = filteredTransactions => {
		const { transactions } = this.props;

		return new Set(filteredTransactions.map(filteredTransaction => transactions.findIndex(transaction => transaction === filteredTransaction)));
	};

	onExport = async e => {
		let { clientId, transactions } = this.props;
		let { priorYearTrialBalance, classifications, hasTax } = this.state;

		e.preventDefault();

		await this.update({
			loading: true
		});

		let filteredTransactions = transactions.map(transaction => {
			let { date, description, merchant, amount, classification, accountName, tax } = transaction;

			let taxAmount = BookkeepingService.calculateTaxAmount({ amount, tax, hasTax });

			return { date, description, merchant, amount, classification, accountName, taxAmount };
		});

		priorYearTrialBalance = priorYearTrialBalance ? priorYearTrialBalance : [];

		await BookkeepingService.downloadBookkeepingSummaries({
			clientId,
			transactions: filteredTransactions,
			priorYearTrialBalance,
			classifications,
			hasTax
		});

		await this.update({
			loading: false
		});
	};

	onClassify = async e => {
		e.preventDefault();

		const { clientId, transactions, onTransactionsUpdate } = this.props;
		const { classifications, industry, province, generalLedgerReferences } = this.state;
		const glRefs = generalLedgerReferences || {};

		const { classifiedTransactions, unclassifiedTransactions } = BookkeepingService.getClassifiedAndUnclassifiedTransactions({ transactions });

		if (unclassifiedTransactions.length === 0) return;

		// Grow references as we classify, so each subsequent call benefits from prior results
		let classificationReferences = classifiedTransactions.map(({ classification, description }) => ({ classification, description }));

		// Mark all unclassified transactions as pending
		let workingTransactions = transactions.map(t =>
			unclassifiedTransactions.includes(t) ? { ...t, aiClassifying: true } : t
		);
		onTransactionsUpdate(workingTransactions);
		await this.update({ classifyingAi: true });

		// Record which indices in workingTransactions are pending (preserves order)
		const pendingIndices = workingTransactions.reduce((acc, t, i) => {
			if (t.aiClassifying) acc.push(i);
			return acc;
		}, []);

		const aiTaxMap = { ...this.state.classificationTaxMap };

		for (let i = 0; i < unclassifiedTransactions.length; i++) {
			const transaction = unclassifiedTransactions[i];
			const idx = pendingIndices[i];

			try {
				const result = await BookkeepingService.categorizeTransaction({
					clientId,
					transactionDescription: transaction.description + " for an amount of $" + transaction.amount,
					amount: transaction.amount,
					classifications,
					classificationReferences,
					industry,
					province,
					generalLedgerReferences: glRefs
				});

				const rawConfidence = result?.confidence;
				const confidence = rawConfidence != null ? (rawConfidence > 1 ? rawConfidence / 100 : rawConfidence) : null;
				const classification = result?.classification || "Unclassified";
				// Use already-established tax from Tax Settings if available, otherwise fall back to AI suggestion
				const tax = aiTaxMap[classification] || result?.tax || "";

				if (classification && classification !== "Unclassified") {
					classificationReferences = [...classificationReferences, { classification, description: transaction.description }];
				}
				if (classification && tax && !aiTaxMap[classification]) {
					aiTaxMap[classification] = tax;
				}

				workingTransactions = workingTransactions.map((t, j) =>
					j === idx ? { ...t, classification, tax, confidence, aiClassifying: false } : t
				);
			} catch {
				workingTransactions = workingTransactions.map((t, j) =>
					j === idx ? { ...t, aiClassifying: false } : t
				);
			}

			onTransactionsUpdate([...workingTransactions]);
		}

		await this.update({ classifyingAi: false, classificationTaxMap: aiTaxMap });
		UtilityService.alert({ type: "success", text: "AI classification complete." });
	};

	updateTransactionTaxes({ transactions, classifications }) {
		let taxMap = Object.fromEntries(
			classifications.map(classification => [classification.account, classification.taxRate])
		);
	
		transactions.forEach(transaction => {
			if (taxMap[transaction.classification]) {
				transaction.tax = taxMap[transaction.classification];
			}
		});
	
		return transactions;
	};

	onUpload = async e => {
		await this.processReferenceFiles(Array.from(e.target.files));
	};

	processReferenceFiles = async files => {
		let { clientId, transactions, onTransactionsUpdate } = this.props;

		await this.update({ loading: true });

		try {
			const response = await BookkeepingService.uploadCasewareReference({
				clientId,
				files,
				transactions
			});

			let { fullTrialBalanceData, casewareCategorizedAccounts, extractedGeneralLedgerMapping } = response?.data ? response.data : response;

			const priorYearTrialBalanceExtract = fullTrialBalanceData.map(account => ({
				accountNumber: account.accountNumber,
				accountType: account.accountType,
				accountClass: account.accountClass,
				accountDescription: account.accountDescription,
				priorYearAmount: account.priorYearAmount
			}));

			const uploadedClassifications = fullTrialBalanceData
				.filter(account => account.accountNumber && account.accountDescription)
				.map(account => ({
					name: `${account.accountNumber} - ${account.accountDescription}`
				}));
			
			let accountsWithDescriptions = BookkeepingService.mapAccountDescriptionToAccount({ 
				casewareCategorizedAccounts, 
				priorYearTrialBalanceExtract 
			});

			let transactionsWithCategories = transactions.map((transaction, index) => {
				let classification = accountsWithDescriptions[index];
				return { ...transaction, classification, cwClassified: !!(classification && classification !== "Unclassified") };
			});

			const { industry, province } = this.state;
			const classificationsToUse = uploadedClassifications.length > 0 ? uploadedClassifications : this.state.classifications;

			// Phase 1: fetch tax codes only for accounts actually used in transactions (fast)
			const usedNames = new Set(transactionsWithCategories.map(t => t.classification).filter(Boolean));
			const usedClassifications = classificationsToUse.filter(c => usedNames.has(c.name));
			const remainingClassifications = classificationsToUse.filter(c => !usedNames.has(c.name));

			const usedTaxResults = await Promise.all(
				usedClassifications.map(c => BookkeepingService.getTaxRates({ clientId, account: c.name, province, industry }))
			);

			const classificationTaxMap = Object.fromEntries(
				usedClassifications.map((c, i) => [c.name, usedTaxResults[i]]).filter(([, tax]) => tax)
			);

			transactionsWithCategories = transactionsWithCategories.map(transaction => ({
				...transaction,
				tax: classificationTaxMap[transaction.classification] || transaction.tax || ""
			}));

			await this.update({
				classifications: classificationsToUse,
				priorYearTrialBalance: priorYearTrialBalanceExtract,
				classificationTaxMap: { ...this.state.classificationTaxMap, ...classificationTaxMap },
				loading: false,
				generalLedgerReferences: extractedGeneralLedgerMapping || {}
			});

			onTransactionsUpdate(transactionsWithCategories);

			// Phase 2: fetch remaining accounts in the background
			if (remainingClassifications.length > 0) {
				Promise.all(
					remainingClassifications.map(c => BookkeepingService.getTaxRates({ clientId, account: c.name, province, industry }))
				).then(remainingResults => {
					const remainingMap = Object.fromEntries(
						remainingClassifications.map((c, i) => [c.name, remainingResults[i]]).filter(([, tax]) => tax)
					);
					this.update({
						classificationTaxMap: { ...this.state.classificationTaxMap, ...remainingMap }
					});
				});
			}
		} catch (error) {
			console.error("Upload failed:", error);
			await this.update({
				loading: false
			});
		}
	};

	getSortedClassifications = () => {
		return [...this.state.classifications].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
	};

	checkBoxSelected = async () => {
		await this.update({
			hasTax: !this.state.hasTax
		});
	};

	calculateTaxAmount = ({ amount, tax }) => {
		let { hasTax } = this.state;

		if (!hasTax) return 0.0;

		let taxAmount = BookkeepingService.calculateTaxAmount({ amount, tax, hasTax });

		return taxAmount;
	};

	sortBy = async field => {
		let { sortOrder } = this.state;

		await this.update({
			sortField: field,
			sortOrder: sortOrder === "asc" ? "desc" : "asc"
		});

		this.sortTransactions();
	};

	sortTransactions = () => {
		const { sortField, sortOrder } = this.state;
		const { transactions, onTransactionsUpdate } = this.props;

		if (!sortField) return;

		const sortedTransactions = [...transactions].sort((a, b) => {
			if (a[sortField] < b[sortField]) return sortOrder === "asc" ? -1 : 1;
			if (a[sortField] > b[sortField]) return sortOrder === "asc" ? 1 : -1;
			return 0;
		});

		onTransactionsUpdate(sortedTransactions);
	};

	renderConfidence = (confidence, cwClassified) => {
		if (cwClassified && (confidence === null || confidence === undefined)) {
			return <span>CW Auto</span>;
		}
		if (confidence === null || confidence === undefined) return <span style={{ color: "#ccc" }}>—</span>;
		const pct = Math.round(confidence * 100);
		const color = pct >= 80 ? "#22a55b" : pct >= 50 ? "#e88c00" : "#e53e3e";
		return <span style={{ fontWeight: 600, color }}>{pct}%</span>;
	};

	renderSortIcon = field => {
		let { sortField, sortOrder } = this.state;

		return sortField === field && (sortOrder === "asc" ? <Icon.ChevronUp size={14} /> : <Icon.ChevronDown size={14} />);
	};

	renderFilteringSection = () => {
		const { search, classificationFilter, bankFilter, showUnclassifiedOnly } = this.state;

		return (
			<div>
				<br />
				<div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
					<Button
						text="Unclassified only"
						view="secondary"
						onClick={() => this.update({ showUnclassifiedOnly: !showUnclassifiedOnly })}
						style={showUnclassifiedOnly ? { background: "#395395", color: "white" } : {}}
					/>
					<div style={{ flex: 1 }}>
						<SearchInput
							placeholder="Filter by Transactions..."
							onChange={(e) => this.update({ search: e.target.value })}
							value={search}
						/>
					</div>
					<div style={{ flex: 1 }}>
						<SearchInput
							placeholder="Filter by Classification..."
							onChange={(e) => this.update({ classificationFilter: e.target.value })}
							value={classificationFilter}
						/>
					</div>
					<div style={{ flex: 1 }}>
						<SearchInput
							placeholder="Filter by Bank..."
							onChange={(e) => this.update({ bankFilter: e.target.value })}
							value={bankFilter}
						/>
					</div>
				</div>
			</div>
		);
	};
	
	renderClientHeadings = () => {
		const { bulkClassificationDropdownOpen, hasTax } = this.state;
		const { onNewImport } = this.props;

		const sortedClassifications = this.getSortedClassifications();

		const {
			checkBoxSelected,
			handleBulkClassificationClick,
			handleBulkClassificationChange,
			onExport,
			onUpload,
		} = this;

		return (
			<div style={{ display: "flex", alignItems: "center", gap: 13 }}>
				<Button text="New Import" view="secondary" onClick={onNewImport} icon={<Icon.RefreshCw size={16} />} />

				{this.renderClassifyButton()}

				<Button text="Tax Settings" view="secondary" onClick={this.openClassificationTaxModal} icon={<Icon.Settings size={18} />} />

				<div className="classification-wrapper">
					<Button text="Classifications" view="secondary" onClick={handleBulkClassificationClick} icon={<Icon.ChevronsDown size={18} />} />

					{bulkClassificationDropdownOpen && (
						<div style={{ position: "absolute", zIndex: 1000, background: "white", border: "1px solid #ccc", boxShadow: "0 4px 6px rgba(0,0,0,0.1)", borderRadius: 4, minWidth: 150, padding: "5px 0" }}>
							{sortedClassifications.map((classification) => (
								<div
									key={classification.name}
									style={{ padding: "8px 12px", cursor: "pointer" }}
									onClick={() => handleBulkClassificationChange(classification.name)}
								>
									{classification.name}
								</div>
							))}
						</div>
					)}
				</div>
				<Button view="secondary" onClick={this.openAddClassificationModal} icon={<Icon.Plus size={18} />} />
				<Button text="Download General Ledger" view="secondary" onClick={onExport} icon={<Icon.DownloadCloud size={18} />} style={{ minWidth: 200 }} />
				<div className="tax-checkbox">
					<Checkbox title="Taxes applicable" checked={hasTax} onChange={checkBoxSelected} />
				</div>
			</div>
		);
	};
	

	renderClassificationTaxModal = () => {
		const { province, classificationTaxMap, classificationTaxModalOpen, taxSettingsLoading, taxModalSearch } = this.state;

		const sortedClassifications = this.getSortedClassifications();
		const taxOptions = TAX_CATEGORIES[province] || [];
		const hasMissing = sortedClassifications.some(c => !classificationTaxMap[c.name]);

		const filteredClassifications = taxModalSearch
			? sortedClassifications.filter(c => c.name.toLowerCase().includes(taxModalSearch.toLowerCase()))
			: sortedClassifications;

		return (
			<Modal styles={{ modal: { width: 500, maxHeight: "80vh" } }} open={classificationTaxModalOpen} onClose={this.closeClassificationTaxModal} center>
				<div className="modal-title">Tax Settings per Classification</div>

				<div style={{ overflowY: "auto", flex: 1, padding: "16px 20px" }}>
					{taxOptions.length === 0 ? (
						<p style={{ color: "#888", fontSize: 14, padding: "16px 0", textAlign: "center" }}>Please select a province first to see available tax options.</p>
					) : (
						<>
							{hasMissing && (
								<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, background: "#f8f8f8", border: "1px solid #e0e0e0", borderRadius: 6, padding: "8px 12px", marginBottom: 12 }}>
									<span style={{ fontSize: 13, color: "#666" }}>Some accounts have no tax assigned.</span>
									<Button
										text={taxSettingsLoading ? "Fetching..." : "Fetch All Tax Codes"}
										view="secondary"
										icon={taxSettingsLoading ? <Icon.Loader size={14} /> : <Icon.Zap size={14} />}
										onClick={this.onFetchAllTaxCodes}
										disabled={taxSettingsLoading}
									/>
								</div>
							)}
							<div style={{ marginBottom: 12 }}>
								<SearchInput
									placeholder="Search accounts..."
									value={taxModalSearch}
									onChange={e => this.update({ taxModalSearch: e.target.value })}
								/>
							</div>
							<div style={{ overflowY: "auto", flex: 1 }}>
								<div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0 8px", fontWeight: 600, fontSize: 13, color: "#555", borderBottom: "2px solid #e0e0e0", marginBottom: 4 }}>
									<span>Classification</span>
									<span>Tax</span>
								</div>
								{filteredClassifications.map(classification => (
									<div key={classification.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f0f0f0", gap: 12 }}>
										<span style={{ fontSize: 14, flex: 1 }}>{classification.name}</span>
										<select
											style={{ padding: "4px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, minWidth: 150, cursor: "pointer" }}
											value={classificationTaxMap[classification.name] || ""}
											onChange={e => this.handleClassificationTaxChange(classification.name, e.target.value)}
										>
											<option value="">— No default —</option>
											{taxOptions.map(tax => (
												<option key={tax} value={tax}>{tax}</option>
											))}
										</select>
									</div>
								))}
							</div>
						</>
					)}
				</div>

				<div className="modal-footer">
					<Button text="Close" view="secondary" onClick={this.closeClassificationTaxModal} />
				</div>
			</Modal>
		);
	};

	renderAddClassificationModal = () => {
		const { addClassificationModalOpen, newClassificationName, newClassificationTax, province } = this.state;
		const taxOptions = TAX_CATEGORIES[province] || [];

		return (
			<Modal styles={{ modal: { width: 420 } }} open={addClassificationModalOpen} onClose={this.closeAddClassificationModal} center>
				<div className="modal-title">Add Classification</div>
				<div className="modal-content">
					<div>
						<label style={{ fontSize: 13, fontWeight: 600, color: "#555", display: "block", marginBottom: 6 }}>Account Name</label>
						<input
							style={{ width: "100%", padding: "8px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 14, boxSizing: "border-box" }}
							value={newClassificationName}
							onChange={e => this.update({ newClassificationName: e.target.value })}
							placeholder="e.g. Office Supplies"
							autoFocus
						/>
					</div>
					<div>
						<label style={{ fontSize: 13, fontWeight: 600, color: "#555", display: "block", marginBottom: 6 }}>Tax Classification</label>
						<select
							style={{ width: "100%", padding: "8px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 14, cursor: "pointer" }}
							value={newClassificationTax}
							onChange={e => this.update({ newClassificationTax: e.target.value })}
						>
							<option value="">— No default —</option>
							{taxOptions.map(tax => (
								<option key={tax} value={tax}>{tax}</option>
							))}
						</select>
						{taxOptions.length === 0 && (
							<p style={{ margin: "6px 0 0", fontSize: 12, color: "#999" }}>Select a province in step 3 to see tax options.</p>
						)}
					</div>
				</div>
				<div className="modal-footer">
					<Button text="Cancel" view="secondary" onClick={this.closeAddClassificationModal} />
					<Button text="Add" onClick={this.onAddClassification} disabled={!newClassificationName.trim()} icon={<Icon.Plus size={16} />} />
				</div>
			</Modal>
		);
	};

	renderClassifyButton() {
		const { industry, province, classifyingAi } = this.state;
		const { transactions } = this.props;

		const isDisabled = classifyingAi || !province || !industry || !transactions || transactions.length === 0;

		return (
			<Button
				text={classifyingAi ? "Classifying..." : "Classify with AI"}
				view="secondary"
				onClick={isDisabled ? null : this.onClassify}
				icon={<Icon.Send size={18} />}
				disabled={isDisabled}
			/>
		);
	}

	render() {
		const { activeClassificationDropdown, loading, selectAll, bookkeepingTableHeadings } = this.state;

		if (loading) {
			return (
				<div className="container-loader">
					<Loader />
				</div>
			);
		}

		const sortedClassifications = this.getSortedClassifications();
		const filteredTransactions = this.getFilteredTransactions();

		return (
			<div className="bookkeeping-tables">
				{this.renderClassificationTaxModal()}
				{this.renderAddClassificationModal()}
				{this.renderClientHeadings()}

				{this.renderFilteringSection()}
				<div className="scroll-container">
					<div className={"t t--compact"}>
						<div className={"t__header"}>
							<div className="t__header__item clients__header__item--checkbox">
								<label className={clsx("checkbox", "checkbox--small", "engs-row-item--checkbox")}>
									<input type="checkbox" checked={selectAll} onChange={this.handleSelectAll} />
								</label>
							</div>
							{bookkeepingTableHeadings.map((heading, index) => (
								<div key={index} className="t__header__item" style={heading.style} onClick={() => this.sortBy(heading.name.toLowerCase())}>
									{heading.name}
									{this.renderSortIcon(heading.name.toLowerCase())}
								</div>
							))}
						</div>

						{filteredTransactions.map((transaction, index) => {
							{
								let { date, description, amount, tax, classification, selected, accountName, confidence, cwClassified, aiClassifying } = transaction;
								let odd = index % 2 === 0;

								return (
									<div className={clsx("t__row", { "t__row--stripe": odd })} key={index} onClick={() => this.handleSelectRow(transaction)} style={{ cursor: "pointer" }}>
										<div className="t__row__item clients__row__item--checkbox">
											<label className={clsx("checkbox", "checkbox--small", "engs-row-item--checkbox")}>
												<input type="checkbox" checked={selected} readOnly />
											</label>
										</div>
										<div className="t__row__item" style={{ flex: 1.2 }}>{date}</div>
										<div className="t__row__item" style={{ flex: 3 }}>{description}</div>
										<div className="t__row__item" style={{ flex: 1 }}>{amount.toFixed(2)}</div>
										<div className="t__row__item" style={{ flex: 1.2 }}>
										{aiClassifying ? (
											<span className="ai-classifying-dots">
												<span>.</span><span>.</span><span>.</span>
											</span>
										) : (tax || "")}
									</div>
										<div className="t__row__item" style={{ flex: 1 }}>
										{aiClassifying ? (
											<span className="ai-classifying-dots">
												<span>.</span><span>.</span><span>.</span>
											</span>
										) : this.calculateTaxAmount({ amount, tax })}
									</div>
										<div className="t__row__item" style={{ flex: 2.5 }}>
											{aiClassifying ? (
												<span className="ai-classifying-dots">
													<span>.</span><span>.</span><span>.</span>
												</span>
											) : (classification || "Unclassified")}
										</div>
										<div className="t__row__item" style={{ flex: 0.8 }}>
										{aiClassifying ? (
											<span className="ai-classifying-dots">
												<span>.</span><span>.</span><span>.</span>
											</span>
										) : this.renderConfidence(confidence, cwClassified)}
									</div>
										<div className="t__row__item" style={{ flex: 1.5 }}>{accountName}</div>
									</div>
								);
							}
						})}
					{(() => {
						const totalAmount = filteredTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);
						const totalTaxAmount = filteredTransactions.reduce((sum, t) => sum + this.calculateTaxAmount({ amount: t.amount, tax: t.tax }), 0);
						return (
							<div className="t__row" style={{ fontWeight: 700, borderTop: "2px solid #e0e0e0", background: "#f5f5f5" }}>
								<div className="t__row__item clients__row__item--checkbox" />
								<div className="t__row__item" style={{ flex: 1.2 }} />
								<div className="t__row__item" style={{ flex: 3, color: "#555" }}>{filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? "s" : ""}</div>
								<div className="t__row__item" style={{ flex: 1 }}>{totalAmount.toFixed(2)}</div>
								<div className="t__row__item" style={{ flex: 1.2 }} />
								<div className="t__row__item" style={{ flex: 1 }}>{totalTaxAmount.toFixed(2)}</div>
								<div className="t__row__item" style={{ flex: 2.5 }} />
								<div className="t__row__item" style={{ flex: 0.8 }} />
								<div className="t__row__item" style={{ flex: 1.5 }} />
							</div>
						);
					})()}
					</div>
				</div>
			</div>
		);
	}
}

export default BookkeepingTable;
